import { useState, useEffect, useRef } from "react"
import toast from "react-hot-toast"
import type { Equipment, EquipmentInput } from "../hooks/useEquipments"

interface EquipmentFormModalProps {
  initial?: Equipment | null
  onClose: () => void
  onSubmit: (data: EquipmentInput) => Promise<void>
}

export function EquipmentFormModal({ initial, onClose, onSubmit }: EquipmentFormModalProps) {
  const isEdit = !!initial
  const modalRef = useRef<HTMLDivElement>(null)

  const [productName, setProductName] = useState(initial?.productName || "")
  const [deliveryDate, setDeliveryDate] = useState(
    initial?.deliveryDate ? initial.deliveryDate.slice(0, 10) : ""
  )
  const [serialNumber, setSerialNumber] = useState(initial?.serialNumber || "")
  const [supplier, setSupplier] = useState(initial?.supplier || "")
  const [customerMgmtNumber, setCustomerMgmtNumber] = useState(initial?.customerMgmtNumber || "")
  const [tags, setTags] = useState<string[]>(initial?.tags || [])
  const [tagInput, setTagInput] = useState("")
  const [submitting, setSubmitting] = useState(false)

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [onClose])

  const addTag = () => {
    const raw = tagInput.trim().replace(/^#/, "")
    if (!raw) return
    if (tags.includes(raw)) {
      setTagInput("")
      return
    }
    setTags((prev) => [...prev, raw])
    setTagInput("")
  }

  const removeTag = (t: string) => {
    setTags((prev) => prev.filter((x) => x !== t))
  }

  const handleSubmit = async () => {
    if (!productName.trim()) {
      toast.error("제품명을 입력해주세요.")
      return
    }
    setSubmitting(true)
    try {
      await onSubmit({
        productName: productName.trim(),
        deliveryDate: deliveryDate ? new Date(deliveryDate).toISOString() : "",
        serialNumber: serialNumber.trim(),
        supplier: supplier.trim(),
        customerMgmtNumber: customerMgmtNumber.trim(),
        tags,
      })
      toast.success(isEdit ? "장비 정보가 수정되었습니다." : "장비가 등록되었습니다.")
      onClose()
    } catch (error) {
      console.error("장비 저장 실패:", error)
      toast.error("저장 중 오류가 발생했습니다. 다시 시도해주세요.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit ? "장비 정보 수정" : "장비 등록"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              제품명 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="예: 순간온수형 레이저용접기"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">납품일자</label>
              <input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                시리얼번호 <span className="text-gray-400 font-normal">(선택)</span>
              </label>
              <input
                type="text"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="선택사항 · 호기별 시리얼은 상세에서 등록"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">납품업체</label>
              <input
                type="text"
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                placeholder="납품업체명"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">고객사 관리번호</label>
              <input
                type="text"
                value={customerMgmtNumber}
                onChange={(e) => setCustomerMgmtNumber(e.target.value)}
                placeholder="예: 24-생산1-001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">태그</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    addTag()
                  }
                }}
                placeholder="태그 입력 후 Enter (예: 레이저)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg font-medium hover:bg-orange-200 transition-colors"
              >
                추가
              </button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-700 text-sm rounded-full"
                  >
                    #{t}
                    <button
                      type="button"
                      onClick={() => removeTag(t)}
                      className="hover:text-orange-900"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-5 py-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white font-semibold rounded-lg hover:from-orange-700 hover:to-amber-700 transition-all disabled:opacity-60"
          >
            {submitting ? "저장 중..." : isEdit ? "수정" : "등록"}
          </button>
        </div>
      </div>
    </div>
  )
}
